import React, { useState, useMemo, useCallback, useEffect, cloneElement } from 'react';
import { useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext, closestCenter } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToHorizontalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { sortableKeyboardCoordinates, SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import { noop } from '../../utils/noop.esm.js';
import '@popperjs/core';
import { Button } from '../Button/Button.esm.js';
import '@panwds/icons';
import '../Icon/ImgIcon.esm.js';
import '../Icon/SemanticIcons.esm.js';
import '../Button/ProgressButton.esm.js';
import '../Button/CopyButton.esm.js';
import { Select } from '../Select/Select.esm.js';
import '../../utils/constants.esm.js';
import '@headlessui/react';
import 'downshift';
import 'react-dom';
import '../Input/Input.esm.js';
import 'react-intl';
import 'react-virtual';
import '../List/List.esm.js';
import '../List/ListItem.esm.js';
import 'uuid';
import { ScrollButton } from './ScrollButton.esm.js';
import { useTabsContext } from './TabsContext.esm.js';

function tabIdFromButton(button) {
    return button.id.split("-").splice(1).join("-");
}
function scrollTabIntoView(tabEl, containerEl, isVertical) {
    const container = containerEl.getBoundingClientRect();
    const tab = tabEl.getBoundingClientRect();
    const start = isVertical ? "top" : "left";
    const end = isVertical ? "bottom" : "right";
    const scrollDistance = tab[start] < container[start]
        ? tab[start] - container[start]
        : tab[end] > container[end]
            ? tab[end] - container[end]
            : null;
    const top = (isVertical && scrollDistance) || 0;
    const left = (!isVertical && scrollDistance) || 0;
    if ("scrollBy" in containerEl) {
        // skip in JSDOM environment
        /* istanbul ignore next */
        containerEl.scrollBy({ top, left, behavior: "smooth" });
    }
}
const TabList = ({ activeTabId, children, dataMetrics, dataTestId, disabled = false, onActiveChange = noop, overflow = "dropdown", scrollButtonProps = {
    size: "md",
}, ...rest }) => {
    const [focusedTabId, setFocusedTabId] = useState(null);
    const [overflowFound, setOverflowFound] = useState(false);
    const { orientation, draggable } = useTabsContext();
    const tabListRef = React.useRef(null);
    const tabsRef = React.useRef(null);
    const overflowRef = React.useRef(null);
    const isVertical = orientation === "vertical";
    const tabArray = useMemo(() => React.Children.toArray(children).filter(Boolean), [React.Children.toArray(children).length]);
    const [items, setItems] = useState([]);
    const [itemIds, setItemIds] = useState(tabArray.map((tab) => tab.props.id));
    const [overflowItems, setOverflowItems] = useState([]);
    const overFlowScrollButtons = !isVertical && overflow === "scroll-button";
    const overflowMenu = !isVertical && overflow === "dropdown";
    const overflowScroll = !isVertical && overflow === "scroll";
    const { onTabDragStart, onTabDragOver, onTabDragMove, onTabDragEnd, onTabDragCancel, onOverFlowMenuChange, } = rest;
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }));
    const onClick = useCallback((e) => {
        const tabEl = e.target;
        if (!(tabEl instanceof HTMLButtonElement))
            return;
        if (!tabEl.id)
            return;
        const tabID = tabIdFromButton(tabEl);
        onActiveChange(tabID);
        setFocusedTabId(tabID);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const containerEl = tabListRef.current;
        scrollTabIntoView(tabEl, containerEl, isVertical);
    }, [onActiveChange, setFocusedTabId, isVertical]);
    const enabledTabs = items.filter((tab) => { var _a; return !((_a = tab === null || tab === void 0 ? void 0 : tab.props) === null || _a === void 0 ? void 0 : _a.disabled); });
    const onKeyDown = useCallback((e) => {
        const id = tabIdFromButton(e.target);
        // support keyboard for vertical tabs
        const rightArrow = isVertical ? 40 : 39;
        const leftArrow = isVertical ? 38 : 37;
        const focusedTab = enabledTabs.find((tab) => tab.props.id === id);
        let nextFocusedTabIndex = focusedTab
            ? enabledTabs.indexOf(focusedTab)
            : 0;
        if (e.keyCode === rightArrow || e.keyCode === leftArrow) {
            e.preventDefault();
            // Move right
            if (e.keyCode === rightArrow) {
                nextFocusedTabIndex++;
                // If we're at the end, go to the start
                if (nextFocusedTabIndex >= enabledTabs.length) {
                    nextFocusedTabIndex = 0;
                }
            }
            // Move left
            if (e.keyCode === leftArrow) {
                nextFocusedTabIndex--;
                // If we're at the start, move to the end
                if (nextFocusedTabIndex < 0) {
                    nextFocusedTabIndex = enabledTabs.length - 1;
                }
            }
            setFocusedTabId(enabledTabs[nextFocusedTabIndex].props.id);
        }
    }, [children, focusedTabId, setFocusedTabId]);
    /* istanbul ignore next */
    function handleDragEnd(event) {
        const { active, over } = event;
        if (active.id !== (over === null || over === void 0 ? void 0 : over.id)) {
            setItems((items) => {
                const oldIndex = itemIds.indexOf(active.id);
                const newIndex = itemIds.indexOf((over === null || over === void 0 ? void 0 : over.id) || "-1");
                return arrayMove(items, oldIndex, newIndex);
            });
        }
        if (onTabDragEnd) {
            onTabDragEnd(event);
        }
    }
    const switchOverFlowElementPosition = (overflowElemIndex) => {
        const lastIndex = items.length - 1;
        const lastVisibleTab = { ...items[lastIndex] };
        setItems((prev) => {
            return [
                ...prev.slice(0, prev.length - 1),
                { ...overflowItems[overflowElemIndex] },
            ];
        });
        setOverflowItems((prev) => {
            return [
                { ...lastVisibleTab },
                ...prev.slice(0, overflowElemIndex),
                ...prev.slice(overflowElemIndex + 1),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ];
        });
        if (onOverFlowMenuChange) {
            onOverFlowMenuChange({
                over: tabArray[lastIndex].props,
                active: overflowItems[overflowElemIndex].props,
            });
        }
    };
    // check if tabs are overflowing
    const isTabListOverflowing = () => {
        var _a, _b;
        const tabListWidth = ((_a = tabListRef === null || tabListRef === void 0 ? void 0 : tabListRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0;
        let totalWidth = 0;
        for (let i = 0; i < items.length; i++) {
            const domEl = (_b = tabListRef === null || tabListRef === void 0 ? void 0 : tabListRef.current) === null || _b === void 0 ? void 0 : _b.querySelector(`#${items[i].props.id}`);
            const elWidth = (domEl === null || domEl === void 0 ? void 0 : domEl.offsetWidth) || 0;
            totalWidth += elWidth;
        }
        return totalWidth > tabListWidth;
    };
    const handleOverflow = () => {
        var _a, _b, _c, _d;
        let totalWidth = 0;
        const tabListWidth = ((_a = tabListRef === null || tabListRef === void 0 ? void 0 : tabListRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0;
        const overflowButtonWidth = ((_b = overflowRef === null || overflowRef === void 0 ? void 0 : overflowRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 0;
        const paddingWidth = 10;
        for (let i = 0; i < items.length; i++) {
            const domEl = (_c = tabListRef === null || tabListRef === void 0 ? void 0 : tabListRef.current) === null || _c === void 0 ? void 0 : _c.querySelector(`#${items[i].props.id}`);
            const elWidth = (domEl === null || domEl === void 0 ? void 0 : domEl.offsetWidth) || 0;
            totalWidth += elWidth;
            const availableWidth = totalWidth + overflowButtonWidth;
            // check the total width of the tabs and if greater than the container, enable overflow dropdown
            if (availableWidth > 0 && availableWidth + paddingWidth > tabListWidth) {
                //splice off the extra tabs and move them inside the overflow dropdown
                const overflowItems = items.splice(i, items.length - 1);
                setOverflowItems((prev) => {
                    prev.unshift(...overflowItems);
                    return [...prev];
                });
                setOverflowFound(true);
                // no need to continue in the loop as we found the threshold width
                break;
            }
            else {
                setOverflowFound(false);
            }
        }
        for (let j = 0; j < overflowItems.length; j++) {
            const domEl = document.querySelector(`#${(_d = overflowItems[j]) === null || _d === void 0 ? void 0 : _d.props.id}`);
            const overFlowElWidth = (domEl === null || domEl === void 0 ? void 0 : domEl.offsetWidth) || 0;
            //while resizing, if a space is generated for a tab, put the tab from overflow dropdown back to the header
            if (totalWidth + overFlowElWidth + overflowButtonWidth + paddingWidth <=
                tabListWidth) {
                setOverflowItems((prev) => {
                    return [...prev.slice(0, j), ...prev.slice(j + 1)];
                });
                setItems((prev) => {
                    return [...prev, { ...overflowItems[j] }];
                });
                // no need to continue in the loop as we found the threshold width
                break;
            }
        }
    };
    useEffect(() => {
        // adjust the tabs positions when resizing the window or container
        if (!isVertical && overflow === "dropdown") {
            window.addEventListener("resize", handleOverflow);
        }
        return function () {
            return window.removeEventListener("resize", handleOverflow);
        };
    });
    useEffect(() => {
        setItemIds(items.map((item) => item.props.id));
        // overflow dropdown is applicable to horizontal tabs only
        if (!isVertical && overflow === "dropdown") {
            handleOverflow();
        }
    }, [items]);
    // store latest interval-id locally so that interval can be cleared when scrolling stops
    let intervalId;
    // start scrolling, set the interval
    const scrollStart = (val) => {
        clearInterval(intervalId);
        intervalId = setInterval(
        /* istanbul ignore next */
        () => {
            if (tabsRef.current) {
                tabsRef.current.scrollLeft += val;
            }
        }, 20);
    };
    // stop scrolling, clear the interval
    const scrollStop = () => {
        clearInterval(intervalId);
    };
    useEffect(() => {
        setOverflowItems([]);
        setItems(tabArray);
    }, [tabArray]);
    return (React.createElement("div", { "data-metrics": dataMetrics, "data-testid": dataTestId || "tab-list", role: "tablist", className: classNames("tw-flex tw-border-0 tw-box-border tw-relative tw-bg-white dark:tw-bg-gray-1050", overflowScroll &&
            "tw-overflow-auto tw-scrollbar-small tw-scrollbar-no-track dark:tw-scrollbar-thumb-dark dark:tw-scrollbar-corner-dark", isVertical
            ? "tw-flex-col tw-overflow-auto tw-scrollbar-no-track dark:tw-scrollbar-thumb-dark dark:tw-scrollbar-corner-dark tw-h-full"
            : "tw-w-full tw-border-solid tw-border-b tw-border-gray-300 dark:tw-border-gray-950"), ref: tabListRef, onClick: onClick },
        overFlowScrollButtons && isTabListOverflowing() && (React.createElement(ScrollButton, { dataTestId: "scroll-left", onMouseDown: () => scrollStart(-10), onMouseUp: scrollStop, onKeyDown: (e) => {
                if (e.key === "Enter" || e.code === "Space") {
                    scrollStart(-10);
                }
            }, onKeyUp: scrollStop, onMouseLeave: scrollStop, direction: "left", ...scrollButtonProps })),
        React.createElement("div", { className: classNames("tw-flex", overFlowScrollButtons && "tw-overflow-auto tw-hide-scrollbar") },
            React.createElement("div", { className: classNames("tw-flex tw-overflow-auto tw-scrollbar-no-track dark:tw-scrollbar-thumb-dark dark:tw-scrollbar-corner-dark", overFlowScrollButtons && "tw-hide-scrollbar", isVertical && "tw-flex-col"), onKeyDown: onKeyDown, ref: tabsRef },
                React.createElement(DndContext, { sensors: sensors, collisionDetection: closestCenter, autoScroll: false, modifiers: [
                        isVertical ? restrictToVerticalAxis : restrictToHorizontalAxis,
                        restrictToParentElement,
                    ], onDragEnd: handleDragEnd, onDragStart: onTabDragStart, onDragOver: onTabDragOver, onDragMove: onTabDragMove, onDragCancel: onTabDragCancel },
                    React.createElement(SortableContext, { items: itemIds, strategy: isVertical
                            ? verticalListSortingStrategy
                            : horizontalListSortingStrategy }, items.map((tab) => {
                        return cloneElement(tab, {
                            ...tab.props,
                            key: tab.props.id,
                            id: tab.props.id,
                            active: activeTabId === tab.props.id,
                            disabled: disabled || tab.props.disabled,
                            draggable,
                            hasFocus: focusedTabId !== null && focusedTabId === tab.props.id,
                        });
                    })))),
            overflowMenu && (React.createElement(Select, { dataTestId: "overflowMenu", buttonRef: overflowRef, button: React.createElement(Button, { isMenu: true, id: "overflowTrigger", dataTestId: "overflowTrigger", addClassName: classNames("tw-text-gray-600", (isVertical || !overflowFound) && "tw-invisible"), appearance: "clear", style: {
                        position: "absolute",
                        right: 0,
                        padding: "4px 4px 4px 0",
                        margin: 0,
                        borderRadius: 0,
                    }, onClick: (e) => e.stopPropagation() }), formatOption: {
                    view: ({ context }) => context === null || context === void 0 ? void 0 : context.node,
                }, listItemSize: "lg", items: overflowItems.map((item) => ({
                    value: item === null || item === void 0 ? void 0 : item.props.children,
                    context: {
                        node: item,
                    },
                })), onChange: (e) => {
                    const { highlightedIndex = -1 } = e;
                    switchOverFlowElementPosition(highlightedIndex);
                } }))),
        overFlowScrollButtons && isTabListOverflowing() && (React.createElement(ScrollButton, { dataTestId: "scroll-right", onMouseDown: () => scrollStart(10), onMouseUp: scrollStop, onKeyDown: (e) => {
                if (e.key === "Enter" || e.code === "Space") {
                    scrollStart(10);
                }
            }, onKeyUp: scrollStop, onMouseLeave: scrollStop, direction: "right", ...scrollButtonProps })),
        isVertical && (React.createElement("div", { className: classNames("tw-border-0 tw-border-solid tw-border-gray-300 dark:tw-border-gray-950 tw-absolute tw-w-px tw-h-full tw-left-0 tw-border-l") }))));
};

export { TabList };
//# sourceMappingURL=TabList.esm.js.map
